import { type ElementRef, useEffect, useRef } from 'react'

import styled from 'styled-components'

import {
    AudioDuration,
    AudioPlayButton,
    AudioTag,
    AudioWave
} from 'components-ref/atoms'
import {
    AudioContextProvider,
    useAudioStore
} from 'context/AudioContext/AudioContext'
import { emitter } from 'services/emitter/emitter'
import { EventsEmitter } from 'services/emitter/events'
import colors from 'styles/colors'

interface IAudioMinimizedProps {
    mediaUrl: string
}

export const AudioMessagePost = ({ mediaUrl }: IAudioMinimizedProps) => {
    return (
        <AudioContextProvider audioReady={true} mediaUrl={mediaUrl}>
            <AudioWrapper />
        </AudioContextProvider>
    )
}

const AudioWrapper = () => {
    const { setPlaying } = useAudioStore()
    const audioRef = useRef<ElementRef<'audio'>>(null)

    useEffect(() => {
        const listener = emitter.addListener(
            EventsEmitter.STOP_ALL_AUDIO_POSTS,
            () => {
                if (audioRef?.current) {
                    audioRef?.current?.pause()
                }

                setPlaying(false)
            }
        )

        return () => {
            listener.remove()
        }
    }, [])

    return (
        <>
            <AudioTag ref={audioRef} />
            <Wrapper>
                <WavePlayWrapper>
                    <AudioWaveContainer>
                        <AudioWaveWrapper>
                            <AudioWave
                                waveColor='gray'
                                waveBackgroundColor='transparent'
                                audioRef={audioRef?.current}
                                hasTimer={false}
                            />
                        </AudioWaveWrapper>
                    </AudioWaveContainer>
                    <PlayButtonContainer>
                        <AudioPlayButton audioRef={audioRef} />
                    </PlayButtonContainer>
                </WavePlayWrapper>
                <AudioDurationWrapper>
                    <AudioDuration />
                </AudioDurationWrapper>
            </Wrapper>
        </>
    )
}

const Wrapper = styled.div`
    width: 100%;
    height: auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 1;
`

const WavePlayWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
`

const AudioWaveContainer = styled.div`
    width: 142px;
    height: 100%;
    position: relative;
    flex-shrink: 0;
    display: flex;
    flex: 1;
`

const AudioWaveWrapper = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    padding: 1rem;
    padding-right: 0;
`

const PlayButtonContainer = styled.div`
    padding-right: 1.5rem;
`

const AudioDurationWrapper = styled.div`
    color: ${colors.slateBlueGray};
    font-size: 1.1rem;
`
